<template>
  <div class="language-selector-container">
    <div ref="languageSelector" class="language-selector">
      <button
        type="button"
        class="selector-button"
        @click="toggleDropdown"
        :aria-haspopup="'listbox'"
        :aria-expanded="dropdownOpen.toString()"
        :aria-label="`Current language: ${currentLocaleName}. Select language`"
      >
        <span>{{ currentLocaleCode }}</span>
        <img
          :src="greyArrow"
          alt=""
          aria-hidden="true"
          :class="['arrow', { rotate: dropdownOpen }]"
        />
      </button>

      <ul
        v-if="dropdownOpen"
        class="dropdown-menu"
        role="listbox"
        @keydown.down.prevent="focusNextOption"
        @keydown.up.prevent="focusPrevOption"
        @keydown.esc.prevent="closeDropdown"
      >
        <li
          v-for="(loc, index) in otherLocales"
          :key="loc.code"
          role="option"
          :aria-selected="loc.code === locale.value"
          tabindex="0"
          @click.stop="changeLocale(loc.code)"
          @keydown.enter.prevent="changeLocale(loc.code)"
          @keydown.space.prevent="changeLocale(loc.code)"
        >
          <img class="dropdown-menu-flag" :src="loc.icon" :alt="`${loc.name} flag`" />
          <span class="locale-name">{{ loc.name }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCookie } from '#app';

const props = defineProps({
  bookingLanguage: {
    type: String,
    default: 'EN'
  }
});

const allLocales = [
  { code: 'en', name: 'English', icon: '/icons/flags/usa.svg' },
  { code: 'es', name: 'Español', icon: '/icons/flags/spain.svg' },
  { code: 'fr', name: 'Français', icon: '/icons/flags/france.svg' },
  { code: 'it', name: 'Italiano', icon: '/icons/flags/italy.svg' },
  { code: 'pt', name: 'Português', icon: '/icons/flags/portugal.svg' }
];

const supportedCodes = allLocales.map(l => l.code);

const greyArrow = '/icons/accordion-more-gray.svg';
const dropdownOpen = ref(false);
const languageSelector = ref(null);
const announcementText = ref('');

const { locale, setLocale } = useI18n();

const currentLocaleCode = computed(() => locale.value?.toUpperCase() || '');
const currentLocaleName = computed(() => {
  const found = allLocales.find(l => l.code === locale.value);
  return found?.name || '';
});
const otherLocales = computed(() => allLocales.filter(l => l.code !== locale.value));

function normalizeToCode(input) {
  if (!input) return '';
  const val = String(input).trim().toLowerCase();
  return supportedCodes.includes(val) ? val : '';
}

// Always override i18n cookie with booking language (when valid), before render.
const i18nCookie = useCookie('i18n_redirected', {
  path: '/',
  sameSite: 'lax',
  secure: process.env.VUE_APP_ENV === 'production',
  maxAge: 60 * 60 * 24 * 365
});

const bookingCode = normalizeToCode(props.bookingLanguage);
const cookieCode = normalizeToCode(i18nCookie.value);

let target = 'en';
if (bookingCode) {
  // Booking language wins every time: persist + use it.
  i18nCookie.value = bookingCode;
  target = bookingCode;
} else if (cookieCode) {
  // No valid booking: respect existing cookie.
  target = cookieCode;
}

if (locale.value !== target) {
  await setLocale(target);
}

// If bookingLanguage prop changes later, keep overriding cookie + locale.
watch(
  () => props.bookingLanguage,
  async (val) => {
    const newCode = normalizeToCode(val);
    if (newCode) {
      i18nCookie.value = newCode;      // override on every change
      if (locale.value !== newCode) {
        await setLocale(newCode);
      }
    }
  }
);

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value;
  if (dropdownOpen.value) {
    nextTick(() => {
      const firstOption = languageSelector.value?.querySelector('li[tabindex="0"]');
      firstOption?.focus();
    });
  }
}

function closeDropdown() {
  dropdownOpen.value = false;
  nextTick(() => {
    const toggleBtn = languageSelector.value?.querySelector('.selector-button');
    toggleBtn?.focus();
  });
}

async function changeLocale(code) {
  dropdownOpen.value = false;
  await setLocale(code); // updates i18n cookie via @nuxtjs/i18n too

  const locObj = allLocales.find(l => l.code === code);
  if (locObj) {
    announcementText.value = `Language changed to ${locObj.name}`;
    setTimeout(() => { announcementText.value = ''; }, 2000);
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'click_page_language_selection');
  }
}

function focusNextOption() {
  const items = Array.from(languageSelector.value.querySelectorAll('li[role="option"]'));
  const idx = items.indexOf(document.activeElement);
  const nextIdx = idx < items.length - 1 ? idx + 1 : 0;
  items[nextIdx].focus();
}

function focusPrevOption() {
  const items = Array.from(languageSelector.value.querySelectorAll('li[role="option"]'));
  const idx = items.indexOf(document.activeElement);
  const prevIdx = idx > 0 ? idx - 1 : items.length - 1;
  items[prevIdx].focus();
}

function handleClickOutside(e) {
  if (dropdownOpen.value && languageSelector.value && !languageSelector.value.contains(e.target)) {
    closeDropdown();
  }
}

function handleFocusIn(e) {
  const newFocused = e.target;
  if (dropdownOpen.value && languageSelector.value && !languageSelector.value.contains(newFocused)) {
    closeDropdown();
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('focusin', handleFocusIn);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('focusin', handleFocusIn);
});
</script>